import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Unpack Zip Documents
 *
 * Generic node that takes one Document or a list of them and, for every one
 * that is a zip archive, files each entry inside it as its own Document. Each
 * unpacked Document carries the zip's associations, so it shows up wherever
 * the zip did (an operator download stays an operator download of the same
 * run, and so stays on Pricing → Vendor Portals). The zip is deleted once its
 * entries are filed, unless `keepZip` is set. A Document that is not a zip is
 * passed through unchanged. A Document that no longer exists is skipped and
 * listed in `missingDocumentIds`, so one file deleted mid-run does not stop the
 * rest from being unpacked.
 *
 * Unlike `unzip_document`, which takes one File and is fanned out per item,
 * this takes the whole list in one step, so a run that downloaded hundreds of
 * archives unpacks them without a child run per archive.
 */
export const unpackZipDocumentsStep = defineStepConfig({
  type: "unpack_zip_documents",
  name: "Unpack Zip Documents",
  description:
    "Unpack every zip among the given Documents into one Document per entry (non-zips pass through)",
  category: "Transformation",

  inputSchema: z.object({
    documentIds: z
      .array(z.string())
      .default([])
      .describe(
        "Documents to unpack, e.g. web_browser's documentIds output. Non-zips pass through.",
      ),
    documentId: z
      .string()
      .optional()
      .describe("A single Document to unpack, when there is only one."),
  }),

  outputSchema: z.object({
    documentIds: z
      .array(z.string())
      .describe(
        "The unpacked entries' Documents, plus any input that was not a zip",
      ),
    file: z
      .array(entityOfType(SystemEntity.CORE_FILE))
      .describe("The File behind each output Document, in the same order"),
    fileName: z
      .array(z.string())
      .describe("Each output Document's file name, in the same order"),
    unpackedZips: z
      .number()
      .int()
      .nonnegative()
      .describe("How many inputs were zip archives"),
    missingDocumentIds: z
      .array(z.string())
      .describe(
        "Inputs skipped because their Document no longer exists (permanently deleted before this step ran)",
      ),
  }),

  configSchema: z.object({
    fileFilter: z
      .string()
      .optional()
      .describe(
        "Optional regex on the entry path; only matching entries are filed (e.g. '\\.xlsx$'). Leave empty to file every entry.",
      ),
    keepZip: z
      .boolean()
      .default(false)
      .describe(
        "Keep each zip Document after unpacking it (deleted by default)",
      ),
  }),

  execution: {
    // Hundreds of archives in one step: each is downloaded, unpacked and
    // re-uploaded in turn. The handler heartbeats between archives.
    startToCloseTimeout: "30 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },

  emits: [] as const,
});
